/**
 * Inventory stacking. Consumables and materials collapse into a single bag slot
 * with a `qty` count, so the bag stops filling up from loot spam. Gear
 * (weapons/armour) never stacks — every roll is unique.
 *
 * A "stack" is just an item object carrying `qty`. Identical items (same
 * stackKey) merge; using/selling/moving operates in whole stacks or single
 * units. The bag capacity counts SLOTS (stacks), not individual units.
 *
 * SLOT MODEL: the player BAG is a FIXED-SIZE array (length = capacity) with
 * `null` holes — items keep their slot, gaps persist (Minecraft-style). The home
 * STASH is an UNLIMITED PACKED list (no gaps). Functions here take a `cap`:
 *   cap = number → fixed sparse bag (place in first empty hole; null on remove)
 *   cap = null   → unlimited packed list (push on add; splice on remove)
 */
import { RARITY } from "../config/constants.js";

const STACK_KINDS = { potion: 1, food: 1, resource: 1, treasure: 1, fragment: 1 };
export const MAX_STACK = 99;

export function isStackable(it) { return !!(it && STACK_KINDS[it.kind]); }
export function stackCount(it) { return it && it.qty ? it.qty : 1; }

/** Items sharing a stackKey are identical and merge into one stack. */
export function stackKey(it) {
  if (!it) return "";
  switch (it.kind) {
    case "potion":   return `potion|${it.name}|${it.potType || ""}|${it.heal || 0}|${it.mana || 0}`;
    case "food":     return `food|${it.id || it.name}`;
    case "resource": return `resource|${it.name}`;
    case "treasure": return `treasure|${it.name}|${it.rarity ? it.rarity.name : ""}|${it.value || 0}`;
    case "fragment": return `fragment|${it.fragId || it.name}`;
    default:         return `${it.kind}|uniq`;   // gear: never matches, never stacks
  }
}

/** Number of occupied (non-null) slots in a list. */
export function slotCount(list) { let n = 0; for (const it of list) if (it) n++; return n; }
/** Index of the first empty (null) slot within `cap`, or -1 if the bag is full. */
export function firstEmpty(list, cap) { for (let i = 0; i < cap; i++) if (!list[i]) return i; return -1; }
/** Ensure a fixed bag array has exactly `cap` slots, padding new ones with null. */
export function padBag(list, cap) { for (let i = list.length; i < cap; i++) list[i] = null; return list; }

/** Can `item` be placed into `list`? (a mergeable stack exists, or a slot is free).
 *  `cap` null → unlimited (home stash); number → fixed bag with null holes. */
export function canAdd(list, item, cap = null) {
  if (isStackable(item)) {
    const key = stackKey(item);
    if (list.some((x) => x && isStackable(x) && stackKey(x) === key && stackCount(x) < MAX_STACK)) return true;
  }
  if (cap == null) return true;                 // unlimited packed list (stash)
  return firstEmpty(list, cap) !== -1;          // fixed bag → any empty hole
}

/** Add `item` to `list`, merging into a compatible stack when possible.
 *  Returns "merged" | "added" | "full". */
export function addItem(list, item, cap = null) {
  if (!item) return "full";
  if (isStackable(item)) {
    const key = stackKey(item);
    const add = stackCount(item);
    const existing = list.find((x) => x && isStackable(x) && stackKey(x) === key && stackCount(x) < MAX_STACK);
    if (existing) { existing.qty = stackCount(existing) + add; return "merged"; }
  }
  if (isStackable(item)) item.qty = stackCount(item);   // normalize so every stackable carries qty
  if (cap == null) { list.push(item); return "added"; } // unlimited packed list (stash)
  const slot = firstEmpty(list, cap);                    // fixed bag → first empty hole
  if (slot === -1) return "full";
  list[slot] = item;
  return "added";
}

// Sort order for Tidy: gear first, then consumables, materials, memories, coins.
const KIND_ORDER = { weapon: 0, armor: 1, potion: 2, food: 3, resource: 4, treasure: 5, fragment: 6, coins: 7 };

/**
 * Tidy the bag in place: merge identical stackables (splitting any overflow into
 * full MAX_STACK stacks), then sort by kind → rarity (high first) → name, and
 * compact everything to the front (gaps fall to the end). A deliberate one-click
 * action — normal play keeps whatever manual arrangement the player set.
 */
export function tidyBag(list, cap) {
  const merged = [];
  const byKey = new Map();
  for (const it of list) {
    if (!it) continue;
    if (isStackable(it)) {
      const key = stackKey(it);
      const cur = byKey.get(key);
      if (cur) { cur.qty = stackCount(cur) + stackCount(it); }
      else { const copy = { ...it, qty: stackCount(it) }; byKey.set(key, copy); merged.push(copy); }
    } else merged.push(it);
  }
  // split any stack that grew past MAX_STACK into multiple full stacks
  const out = [];
  for (const it of merged) {
    if (isStackable(it) && stackCount(it) > MAX_STACK) {
      let q = stackCount(it);
      while (q > 0) { const take = Math.min(MAX_STACK, q); out.push({ ...it, qty: take }); q -= take; }
    } else out.push(it);
  }
  out.sort((a, b) => {
    const ka = KIND_ORDER[a.kind] ?? 9, kb = KIND_ORDER[b.kind] ?? 9;
    if (ka !== kb) return ka - kb;
    const ra = a.rarity ? RARITY.indexOf(a.rarity) : -1, rb = b.rarity ? RARITY.indexOf(b.rarity) : -1;
    if (ra !== rb) return rb - ra;                 // higher rarity first
    return (a.name || "").localeCompare(b.name || "");
  });
  for (let i = 0; i < cap; i++) list[i] = i < out.length ? out[i] : null;
  return list;
}

/** Remove ONE unit from the stack at `idx`. Returns a single-unit item, or null.
 *  `packed` true → splice out (stash); false → leave a null gap (fixed bag). */
export function takeOne(list, idx, packed = false) {
  const it = list[idx];
  if (!it) return null;
  if (isStackable(it) && stackCount(it) > 1) {
    it.qty = stackCount(it) - 1;
    return { ...it, qty: 1 };
  }
  if (packed) list.splice(idx, 1); else list[idx] = null;
  const one = { ...it }; delete one.qty;
  return one;
}
